import React, { useContext, useEffect, useMemo, useState } from 'react';

import { getSurroundFinishProfile, Island, Panel } from '@rta/rta-blueprint-library';
import { AppContext } from '@r-app/app-context';
import { DisplayMode } from '@r-core';
import { renderTracking } from '@r-services/render-tracking';

export type RenderOverhangRemovalLineProps = {
	island: Island;
};

const STANDARD_OVERHANG_THRESHOLD = 2;

/**
 * Red line styling constants
 */
const LINE_COLOR = '#FF0000';
const LINE_HEIGHT = 2;
const TEXT_FONT_SIZE = 1.25;
const TEXT_PADDING = 0.4;

/**
 * Calculates the position and dimensions for the red line along a panel's outside edge.
 * Uses countertop width (not panel width) so the line matches the actual countertop length,
 * the same way backsplashes gather their width from the countertop.
 */
const getLinePositionData = (panel: Panel, countertopWidth: number, countertopCenterOffset: number): {
	centerX: number;
	centerZ: number;
	width: number;
	panelAngle: number;
	overhang: number;
} => {
	const panelCenter = panel.center;
	const panelAngle = panel.getOriginAngle();
	const panelDepth = panel.thickness;
	const overhang = panel.countertopOverhang;

	const lineThickness = LINE_HEIGHT;
	// Position behind the overhang (back side of panel) so it doesn't cover backsplash
	const depthOffsetMagnitude = (panelDepth / 2) + overhang + (lineThickness / 2);

	const angleRad = (panelAngle * Math.PI) / 180;
	const depthOffsetX = Math.sin(angleRad) * depthOffsetMagnitude;
	const depthOffsetZ = Math.cos(angleRad) * depthOffsetMagnitude;

	// Apply countertop center offset along the panel's width axis
	// Negate for back/right panels (same convention as backsplash overhead renderer)
	const normalizedAngle = ((panelAngle % 360) + 360) % 360;
	const isBackPanel = Math.abs(normalizedAngle - 180) < 1;
	const isRightPanel = Math.abs(normalizedAngle - 270) < 1;
	const widthOffset = (isBackPanel || isRightPanel) ? -countertopCenterOffset : countertopCenterOffset;

	const widthOffsetX = Math.cos(angleRad) * widthOffset;
	const widthOffsetZ = -Math.sin(angleRad) * widthOffset;

	return {
		centerX: panelCenter.x + depthOffsetX + widthOffsetX,
		centerZ: panelCenter.z + depthOffsetZ + widthOffsetZ,
		width: countertopWidth,
		panelAngle,
		overhang
	};
};

export const RenderOverhangRemovalLine = ({ island }: RenderOverhangRemovalLineProps): React.ReactElement => {
	const { controller } = useContext(AppContext);
	const { kitchen } = controller;

	const [overhangVersion, setOverhangVersion] = useState(0);

	useEffect(() => {
		const subscriptions: Array<string> = [];

		subscriptions.push(kitchen.subscribe('overhang', 'updated', () => {
			setOverhangVersion((v) => v + 1);
		}));

		return () => {
			subscriptions.forEach((subId) => {
				kitchen.unsubscribe(subId);
			});
		};
	}, [kitchen]);

	// Flag only panels whose overhang is below THEIR material's standard. MOK's standard is 0.75"
	// and others are 2", so a MOK panel at its standard is not a removal and shows no marker.
	const surround = island.surround ?? kitchen.material?.surround ?? null;
	const standardOverhang = getSurroundFinishProfile(surround?.material)?.standardOverhang ?? STANDARD_OVERHANG_THRESHOLD;

	const panelsWithLowOverhang = useMemo(() => {
		return island.panels.children.filter((panel: Panel) => panel.countertopOverhang < standardOverhang);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [island.panels.children, overhangVersion, standardOverhang]);

	renderTracking.incrementRender('RenderOverhangRemovalLine');

	const displayMode = controller?.navigation?.displayMode;
	if (displayMode !== DisplayMode.SkuOverhead && displayMode !== DisplayMode.SkuOverheadManufacturing) {
		return <></>;
	}

	if (panelsWithLowOverhang.length === 0) {
		return <></>;
	}

	return (
		<g type="overhang-removal-lines">
			{panelsWithLowOverhang.map((panel: Panel) => {
				const countertop = island.getCountertop();
				const countertopWidth = countertop?.getCountertopWidthForPanel(panel.id) ?? panel.size.width;
				const countertopCenterOffset = countertop?.getCountertopCenterOffsetForPanel(panel.id) ?? 0;
				const lineData = getLinePositionData(panel, countertopWidth, countertopCenterOffset);
				const { centerX, centerZ, width, panelAngle, overhang } = lineData;

				// Format overhang amount
				const overhangText = `${overhang.toFixed(2)}" overhang`;

				// Use transform group with translate and rotate, matching backsplash approach
				// This positions the line at the outer edge of the countertop overhang
				return (
					<g
						key={`overhang-line-${panel.id}`}
						data-panel-id={panel.id}
						transform={`translate(${centerX}, ${centerZ}) rotate(${-panelAngle})`}
					>
						{/* Red rectangle along countertop edge */}
						<rect
							x={-width / 2}
							y={-LINE_HEIGHT / 2}
							width={width}
							height={LINE_HEIGHT}
							fill={LINE_COLOR}
							rx={0.2}
							ry={0.2}
						/>

						{/* White overhang text at the start of the rectangle */}
						<text
							x={-width / 2 + TEXT_PADDING}
							y={0}
							textAnchor="start"
							dominantBaseline="central"
							fontSize={TEXT_FONT_SIZE}
							fontFamily="Arial, sans-serif"
							fontWeight="bold"
							fill="#FFFFFF"
							className="rta-noselect"
						>
							{overhangText}
						</text>
					</g>
				);
			})}
		</g>
	);
};
